import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { EquityCurve } from '../components/charts/EquityCurve';
import { Button } from '../components/primitives/Button';
import { Chip } from '../components/primitives/Chip';
import { Container } from '../components/primitives/Container';
import { StatBlock } from '../components/primitives/StatBlock';
import { Tag } from '../components/primitives/Tag';
import { useStrategy } from '../content/load';
import { equityCurve } from '../lib/equity-curve';
import { useDocumentTitle } from '../lib/seo';

const Header = styled.header`
  padding: ${(p) => p.theme.step[7]}px 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${(p) => p.theme.step[5]}px;
  align-items: end;
`;

const Code = styled.span`
  font-family: ${(p) => p.theme.font.mono};
  font-size: ${(p) => p.theme.step[1]}px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${(p) => p.theme.color.text[3]};
`;

const Name = styled.h1`
  font-family: ${(p) => p.theme.font.serif};
  font-size: clamp(${(p) => p.theme.step[8]}px, 5vw, ${(p) => p.theme.step[10]}px);
  margin: 8px 0;
  letter-spacing: -0.02em;
`;

const Tagline = styled.p`
  margin: 0;
  font-size: ${(p) => p.theme.step[5]}px;
  color: ${(p) => p.theme.color.text[1]};
`;

const Tags = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const Stats = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${(p) => p.theme.step[5]}px;
  border-top: 1px solid ${(p) => p.theme.color.border.faint};
  border-bottom: 1px solid ${(p) => p.theme.color.border.faint};
  padding: ${(p) => p.theme.step[5]}px 0;
`;

const Description = styled.p`
  font-size: ${(p) => p.theme.step[5]}px;
  color: ${(p) => p.theme.color.text[1]};
  max-width: 720px;
  line-height: 1.55;
  margin: ${(p) => p.theme.step[5]}px 0 0;
`;

const Mechanics = styled.section`
  margin-top: ${(p) => p.theme.step[7]}px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
  gap: ${(p) => p.theme.step[7]}px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export default function Strategy() {
  const { id = '' } = useParams<{ id: string }>();
  const s = useStrategy(id);
  useDocumentTitle(s.name, s.tagline);
  const data = equityCurve({ seed: s.id.length * 11 + s.trades, steps: 240 });
  return (
    <Container>
      <Header>
        <div>
          <Code>{s.code}</Code>
          <Name>{s.name}</Name>
          <Tagline>{s.tagline}</Tagline>
          <Tags>
            <Tag>{s.category}</Tag>
            <Tag>{s.style}</Tag>
            <Chip $variant="amber">{s.tier}</Chip>
            <Chip $variant={s.status === 'live' ? 'up' : 'flat'}>{s.status}</Chip>
          </Tags>
        </div>
        <Button
          $variant="primary"
          onClick={() => alert('Subscribe flow is mock-only in v1.')}
        >
          Subscribe
        </Button>
      </Header>

      <EquityCurve data={data} height={320} />

      <Stats>
        <StatBlock label="SHARPE" value={s.sharpe.toFixed(2)} />
        <StatBlock label="CAGR" value={`${s.cagr}%`} />
        <StatBlock label="MAX DD" value={`${s.maxDD}%`} />
        <StatBlock label="WIN RATE" value={`${s.winRate}%`} />
        <StatBlock label="LIFETIME" value={s.lifetime} />
        <StatBlock label="YTD" value={s.ytd} />
        <StatBlock label="MONTHLY" value={s.monthly} />
        <StatBlock label="AVG HOLD" value={s.avgHold} />
        <StatBlock label="TRADES" value={s.trades.toLocaleString()} />
        <StatBlock label="INCEPTION" value={s.inception} />
        <StatBlock label="MIN CAPITAL" value={`$${s.minCapital.toLocaleString()}`} />
        <StatBlock label="CAPACITY" value={`${s.capacity}% used`} />
      </Stats>

      <Description>{s.description}</Description>

      {s.detail && (
        <Mechanics>
          <div>
            <h2 style={{ fontFamily: 'var(--font-serif)', margin: 0 }}>How it works</h2>
            <p style={{ color: 'var(--text-1)', marginTop: 12 }}>{s.detail.thesis}</p>
          </div>
          <div>
            {s.detail.mechanics.map((m, i) => (
              <section key={i} style={{ marginBottom: 24 }}>
                <h3 style={{ fontFamily: 'var(--font-serif)', margin: '0 0 8px' }}>
                  {m.heading}
                </h3>
                <p style={{ color: 'var(--text-1)', margin: 0 }}>{m.body}</p>
              </section>
            ))}
            {s.detail.sampleCodeBlock && (
              <pre
                style={{
                  background: 'var(--bg-1)',
                  padding: 16,
                  borderRadius: 10,
                  overflow: 'auto',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 13,
                  border: '1px solid var(--border-soft)',
                }}
              >
                <code>{s.detail.sampleCodeBlock}</code>
              </pre>
            )}
          </div>
        </Mechanics>
      )}
    </Container>
  );
}
